/**
 * Notification Settings Component
 * Allows users to configure sound and push notification preferences
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Bell, Volume2, VolumeX, Smartphone, AlertCircle, Crown, Volume1, Shield, Clock, Timer } from 'lucide-react';
import { playNotificationSound, playSubscriptionSound } from '../../utils/notifications';
import { getIdleTimeoutSettings, saveIdleTimeoutSettings } from '../../hooks/useIdleTimeout';

// Inner component that uses the context
const NotificationSettingsInner = ({ wsContext }) => {
  const {
    notificationSettings,
    updateNotificationSettings,
    permissionStatus,
    requestPermission,
    supportsNotifications,
    isConnected
  } = wsContext;

  // Idle timeout settings (separate from notification settings)
  const [idleSettings, setIdleSettings] = useState(getIdleTimeoutSettings);

  // Sync idle settings
  useEffect(() => {
    const handleSettingsChange = (event) => {
      setIdleSettings(event.detail || getIdleTimeoutSettings());
    };
    window.addEventListener('idle-timeout-settings-changed', handleSettingsChange);
    return () => window.removeEventListener('idle-timeout-settings-changed', handleSettingsChange);
  }, []);

  const updateIdleSettings = (updates) => {
    const newSettings = saveIdleTimeoutSettings(updates);
    setIdleSettings(newSettings);
  };

  const handleTestSound = (type) => {
    if (type === 'subscription-upgrade') {
      playSubscriptionSound(true);
    } else if (type === 'subscription-switch') {
      playSubscriptionSound(false);
    } else {
      playNotificationSound(type);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notification Settings
        </CardTitle>
        <CardDescription>
          Configure how you receive notifications
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Connection Status */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="text-sm font-medium">
              {isConnected ? 'Connected (Live)' : 'Disconnected'}
            </span>
          </div>
          {!isConnected && (
            <span className="text-xs text-gray-500">
              Notifications may be delayed
            </span>
          )}
        </div>

        {/* Sound Notifications */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            {notificationSettings.soundEnabled ? (
              <Volume2 className="h-5 w-5 text-teal-600" />
            ) : (
              <VolumeX className="h-5 w-5 text-gray-400" />
            )}
            <h3 className="font-medium">Sound Notifications</h3>
          </div>
          
          <div className="space-y-3 pl-7">
            <div className="flex items-center justify-between">
              <Label htmlFor="sound-enabled">Enable notification sounds</Label>
              <Switch
                id="sound-enabled"
                checked={notificationSettings.soundEnabled}
                onCheckedChange={(checked) => 
                  updateNotificationSettings({ soundEnabled: checked })
                }
              />
            </div>
            
            {notificationSettings.soundEnabled && (
              <>
                {/* Volume Slider */}
                <div className="space-y-3 pb-3 border-b">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <Volume1 className="h-4 w-4 text-gray-500" />
                      Volume
                    </Label>
                    <span className="text-sm text-gray-500 font-medium w-12 text-right">
                      {Math.round((notificationSettings.soundVolume || 0.3) * 100)}%
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <VolumeX className="h-4 w-4 text-gray-400" />
                    <Slider
                      value={[(notificationSettings.soundVolume || 0.3) * 100]}
                      min={0}
                      max={100}
                      step={5}
                      onValueChange={(value) => 
                        updateNotificationSettings({ soundVolume: value[0] / 100 })
                      }
                      className="flex-1"
                      data-testid="volume-slider"
                    />
                    <Volume2 className="h-4 w-4 text-teal-600" />
                  </div>
                  <p className="text-xs text-gray-500">
                    Adjust the volume for all notification sounds
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="message-sound">Message sounds</Label>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleTestSound('message')}
                    >
                      Test
                    </Button>
                    <Switch
                      id="message-sound"
                      checked={notificationSettings.messageSound}
                      onCheckedChange={(checked) => 
                        updateNotificationSettings({ messageSound: checked })
                      }
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <Label htmlFor="appointment-sound">Appointment sounds</Label>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleTestSound('appointment')}
                    >
                      Test
                    </Button>
                    <Switch
                      id="appointment-sound"
                      checked={notificationSettings.appointmentSound}
                      onCheckedChange={(checked) => 
                        updateNotificationSettings({ appointmentSound: checked })
                      }
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <Label htmlFor="urgent-sound">Urgent notification sounds</Label>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleTestSound('urgent')}
                    >
                      Test
                    </Button>
                    <Switch
                      id="urgent-sound"
                      checked={notificationSettings.urgentSound}
                      onCheckedChange={(checked) => 
                        updateNotificationSettings({ urgentSound: checked })
                      }
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Crown className="h-4 w-4 text-yellow-500" />
                    <Label htmlFor="subscription-sound">Subscription change sounds</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleTestSound('subscription-upgrade')}
                    >
                      Test
                    </Button>
                    <Switch
                      id="subscription-sound"
                      checked={notificationSettings.subscriptionSound}
                      onCheckedChange={(checked) => 
                        updateNotificationSettings({ subscriptionSound: checked })
                      }
                      data-testid="subscription-sound-toggle"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Browser Push Notifications */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-teal-600" />
            <h3 className="font-medium">Browser Push Notifications</h3>
          </div>
          
          <div className="space-y-3 pl-7">
            {!supportsNotifications ? (
              <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                <AlertCircle className="h-4 w-4" />
                <span>Your browser does not support push notifications</span>
              </div>
            ) : permissionStatus === 'denied' ? (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                <AlertCircle className="h-4 w-4" />
                <span>Push notifications are blocked. Please enable them in your browser settings.</span>
              </div>
            ) : permissionStatus === 'default' ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  Enable push notifications to receive alerts even when this tab is not active.
                </p>
                <Button onClick={requestPermission} variant="outline" size="sm">
                  Enable Push Notifications
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <Label htmlFor="push-enabled">
                  Show notifications when tab is inactive
                </Label>
                <Switch
                  id="push-enabled"
                  checked={notificationSettings.pushEnabled}
                  onCheckedChange={(checked) => 
                    updateNotificationSettings({ pushEnabled: checked })
                  }
                />
              </div>
            )}
          </div>
        </div>

        {/* Session Security - Idle Timeout */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-red-600" />
            <h3 className="font-medium">Session Security</h3>
          </div>
          
          <div className="space-y-4 pl-7">
            {/* Enable/Disable Auto Sign-Out */}
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="idle-timeout-enabled" className="flex items-center gap-2">
                  <Timer className="h-4 w-4 text-gray-500" />
                  Auto sign-out after inactivity
                </Label>
                <p className="text-xs text-gray-500 mt-1">
                  Automatically sign out when inactive for security
                </p>
              </div>
              <Switch
                id="idle-timeout-enabled"
                checked={idleSettings.enabled}
                onCheckedChange={(checked) => updateIdleSettings({ enabled: checked })}
                data-testid="idle-timeout-toggle"
              />
            </div>

            {/* Timeout Duration Selector */}
            {idleSettings.enabled && (
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    Inactivity timeout
                  </Label>
                  <Select
                    value={String(idleSettings.timeoutMinutes)}
                    onValueChange={(value) => updateIdleSettings({ timeoutMinutes: parseInt(value) })}
                  >
                    <SelectTrigger className="w-[140px]" data-testid="idle-timeout-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 minute (Test)</SelectItem>
                      <SelectItem value="5">5 minutes</SelectItem>
                      <SelectItem value="10">10 minutes</SelectItem>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <p className="text-xs text-gray-500">
                  You'll see a 60-second warning before being signed out. 
                  Activity (clicks, keypress, scroll) resets the timer.
                </p>

                {idleSettings.timeoutMinutes === 1 && (
                  <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 p-2 rounded">
                    <AlertCircle className="h-3 w-3" />
                    <span>1-minute timeout is for testing only. Choose a longer duration for normal use.</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="text-xs text-gray-500 pt-4 border-t">
          <p>
            Sound notifications play when you receive new messages or appointment updates.
            Push notifications appear even when this browser tab is not active.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

// Fallback component when context is not available
const NotificationSettingsFallback = () => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Bell className="h-5 w-5" />
        Notification Settings
      </CardTitle>
      <CardDescription>
        Real-time notifications are not available
      </CardDescription>
    </CardHeader>
  </Card>
);

// Main component that safely tries to use the context
export default function NotificationSettings() {
  // Dynamically import and use the context
  let WebSocketContextModule = null;
  try {
    WebSocketContextModule = require('../../context/WebSocketContext');
  } catch (e) {
    return <NotificationSettingsFallback />;
  }

  if (!WebSocketContextModule || !WebSocketContextModule.useWebSocketContext) {
    return <NotificationSettingsFallback />;
  }

  // Use a wrapper component that calls the hook
  const ContextWrapper = () => {
    try {
      const wsContext = WebSocketContextModule.useWebSocketContext();
      return <NotificationSettingsInner wsContext={wsContext} />;
    } catch (e) {
      return <NotificationSettingsFallback />;
    }
  };

  return <ContextWrapper />;
}
